import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { useRouter } from "next/router";
import { fetchTactics, newTacticRepository } from "@/repositories/tactic";
import { TacticCard } from "@/components/base/ui/TacticCard";
import { SectionTitle } from "@/components/base/ui/SectionTitle";
import { Breadcrumb } from "@/components/base/Breadcrumb";
import Qs from "qs";
import { Head } from "@/components/base/Head";
import { client } from "@/api/client";
import { Tactic } from "@/types/tactic";
import { Loading } from "@/components/base/Loading";
import { ScrollToTop } from "@/components/base/ui/ScrollToTop";

type TacticTag = {
  tagName: string;
  tagId: string;
  items: Tactic[];
};

const TacticsPage = ({ tactics }: { tactics: Tactic[] }) => {
  const META_TITLE = "講座一覧";
  const breadcrumbs = [
    {
      title: META_TITLE,
    },
  ];
  const [tacticTags, setTacticTags] = useState<TacticTag[]>([]);
  const router = useRouter();
  const query = {
    tactic_category_id_eq: router.query.tactic_category_id || "",
    tactic_tags_tag_id_eq: router.query.tag_id || "",
    title_cont: router.query.title || "",
  };
  const { data } = useSWR(
    `/api/v1/tactics?${Qs.stringify(query, { arrayFormat: "brackets" })}`,
    (url) =>
      fetchTactics(url, {
        "access-token": "",
        client: "",
        expiry: "",
        uid: "",
      })
  );

  useEffect(() => {
    const temp: TacticTag[] = [];
    data?.forEach((_data) => {
      if (!_data.tactic_tags[0]) return;
      const target = temp.find(
        (_) => _.tagName === _data.tactic_tags[0]?.name_ja
      );
      if (target) {
        target.items.push(_data);
      } else {
        temp.push({
          tagName: _data.tactic_tags[0].name_ja,
          tagId: _data.tactic_tags[0].id,
          items: [_data],
        });
      }
    });
    setTacticTags(temp);
  }, [data, router.pathname]);

  return (
    <>
      <Head title={META_TITLE} canonicalPath={"/tactics"} />
      <main className={"relative pb-10"}>
        <div className={"mb-10 max-w-[1440px] m-auto px-4 py-2"}>
          <Breadcrumb breadcrumbs={breadcrumbs} />
        </div>
        <div className={"max-w-[1160px] m-auto py-8 px-5"}>
          {data ? (
            <>
              {tacticTags.length &&
                tacticTags.map((tacticTag, index) => (
                  <section className={"mb-20"} key={index}>
                    <SectionTitle title={tacticTag.tagName} />
                    <div
                      className={
                        "grid md:grid-cols-4 grid-cols-2 gap-y-4 gap-x-2"
                      }
                    >
                      {tacticTag.items.map((item, index) => (
                        <div key={index}>
                          <TacticCard tactic={item} />
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
            </>
          ) : (
            <Loading />
          )}
        </div>
        <ScrollToTop />
      </main>
    </>
  );
};

export async function getStaticProps() {
  const tacticsRepository = newTacticRepository(client);
  const query = {
    sort_order_eq: 1,
  };
  const [tactics] = await Promise.all([
    tacticsRepository.listTactics(
      `/api/v1/tactics?${Qs.stringify(query, { arrayFormat: "brackets" })}`
    ),
  ]);

  return {
    props: {
      tactics,
    },
  };
}
export default TacticsPage;
